/** Empty or whitespace-only. */
export function isEmptyInput(s: string): boolean {
	return s.trim() === '';
}

/**
 * Parse a user-entered number. Returns null for empty, NaN-ish, or partial
 * input (e.g., '.' or '-'), else the parsed finite number.
 */
export function parseLenientNumber(s: string): number | null {
	const t = s.trim();
	if (t === '' || t === '.' || t === '-') return null;
	const n = Number(t);
	if (!Number.isFinite(n)) return null;
	return n;
}

/**
 * Treat `''`, `'-'`, `'.'`, `'-.'` as "still typing" so we don't flash an
 * error before the user can finish a partial entry.
 */
function isPartialInput(s: string): boolean {
	const t = s.trim();
	return t === '' || t === '-' || t === '.' || t === '-.';
}

export type FieldKind = 'positive' | 'nonnegative';

/**
 * Validate a single numeric input. Returns a short, user-facing error
 * message, or `null` when the field is empty / partially typed / valid.
 *
 * - `positive`: the value must be a finite number strictly greater than 0
 *   (used for chord length and radius/diameter).
 * - `nonnegative`: the value must be a finite number >= 0 (used for
 *   tolerances, where 0 means "exact").
 */
export function fieldNumericError(
	value: string,
	kind: FieldKind
): string | null {
	if (isPartialInput(value)) return null;
	const n = Number(value.trim());
	if (!Number.isFinite(n)) return 'Enter a valid number.';
	if (n < 0) return 'Cannot be negative.';
	if (kind === 'positive' && n === 0) return 'Must be greater than 0.';
	return null;
}
